import React, { useLayoutEffect, useState } from 'react';

import { pxToRem } from 'utils/cssUnits';

export interface FlyoutPosition {
  top: number;
  left: number;
  width: number;
  height: number;
  flyoutWidth: number;
}

interface UseFlyoutPositionResult {
  position: FlyoutPosition | null;
  portalContainer: HTMLElement | null;
}

const STEP_PICKER_POPOVER_SELECTOR = '.step-input-selector-popover';

// Flyouts are half the anchor's width.
const FLYOUT_WIDTH_RATIO = 0.5;

const isSamePosition = (a: FlyoutPosition | null, b: FlyoutPosition) =>
  !!a &&
  a.top === b.top &&
  a.left === b.left &&
  a.width === b.width &&
  a.height === b.height &&
  a.flyoutWidth === b.flyoutWidth;

/**
 * Measures `anchorRef` against the step picker popover so a flyout can be
 * portaled flush against its right edge.
 * - All values are in rem, relative to the popover container.
 * - Sits flush against the picker's right edge. When that would run past the
 *   window, it slides left by the overflow so it overlaps the picker's right
 *   side instead of leaving the screen.
 * - Re-measures on window resize and on any scroll (capture phase).
 */
const useFlyoutPosition = (
  anchorRef: React.RefObject<HTMLElement>
): UseFlyoutPositionResult => {
  const [position, setPosition] = useState<FlyoutPosition | null>(null);
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(
    null
  );

  useLayoutEffect(() => {
    const updatePosition = () => {
      const anchorEl = anchorRef.current;
      const containerEl =
        anchorEl?.closest<HTMLElement>(STEP_PICKER_POPOVER_SELECTOR) ?? null;
      setPortalContainer(containerEl);
      const anchorRect = anchorEl?.getBoundingClientRect();
      const containerRect = containerEl?.getBoundingClientRect();
      if (!anchorRect || !containerRect) return;
      const flyoutWidth = anchorRect.width * FLYOUT_WIDTH_RATIO;
      const left = Math.max(
        anchorRect.left,
        Math.min(anchorRect.right, window.innerWidth - flyoutWidth)
      );
      const next: FlyoutPosition = {
        top: pxToRem(anchorRect.top - containerRect.top),
        left: pxToRem(left - containerRect.left),
        width: pxToRem(anchorRect.width),
        height: pxToRem(anchorRect.height),
        flyoutWidth: pxToRem(flyoutWidth),
      };
      setPosition((prev) => (isSamePosition(prev, next) ? prev : next));
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [anchorRef]);

  return { position, portalContainer };
};

export default useFlyoutPosition;
